/* eslint-disable default-case */
import React, {useEffect, useState} from 'react';
import {Button, Col, Form, Modal, Row} from 'react-bootstrap';
import {BiEdit, BiTrash} from 'react-icons/bi';
import {Get, Post} from '../../lib/Request';
import {ALERT_VARIANT, PaginationEnum} from '../../const/dataEnum';
import {setAlert} from '../../stores/actions/alertModalStateAction';
import {useDispatch, useSelector} from 'react-redux';
import {API_ACTION, API_PRACTICE} from '../../const/Api';
import DepartmentDropdown from '../../components/CommonDropdown/DepartmentDropdown';
import {isUserAdmin} from '../../stores/business/userBusiness';
import {userAuthRoleIdSelector} from '../../stores/selectors/appStateSelector';
import {getDefaultValue} from '../../lib/utilities';
import EssSimpleTable from '../../components/MessageTable/TableColumn/EssSimpleTable';
import {usePagination} from '../../hooks/usePagination';
import './ManagePractices.scss';

export default function ManagePractices() {
  const dispatch = useDispatch();
  const userAuthRoleId = useSelector(userAuthRoleIdSelector);
  const isAdmin = isUserAdmin(userAuthRoleId);

  const defaultPractice = getDefaultValue('practice');

  // Department
  const [selectedDepartment, setSelectedDepartment] = useState(defaultPractice);

  // Practice
  const [loadingPractices, setLoadingPractices] = useState(true);
  const [practices, setPractices] = useState([]);
  const [practiceName, setPracticeName] = useState('');
  const [practiceID, setPracticeID] = useState(0);
  const [action, setAction] = useState('');
  const [loadingModify, setLoadingModify] = useState(false);

  const columnData = [
    {id: 'index', Header: '#', accessor: 'index', sortable: false},
    {id: 'practiceName', Header: 'Practice Name', accessor: 'practiceName', sortable: false},
    {id: 'action', Header: 'Action', accessor: 'action', sortable: false},
  ];

  const {
    page,
    setPage,
    rowsPerPage,
    slicedData: filteredData,
    handleChangePage,
    handleChangeRowsPerPage,
    pageOptions,
    MemoizedEmptyRows,
  } = usePagination(
    practices.map((item, index) => ({
      id: item.id,
      index: index + 1,
      practiceName: item.practiceName,
      action: '',
    })),
    {},
    PaginationEnum.PAGE_OPTIONS
  );

  const columns = (column, row) => {
    switch (column.accessor) {
      case 'index':
        return row.index;
      case 'practiceName':
        return row.practiceName;
      case 'action':
        return (
          <div className="action-buttons">
            <Button
              onClick={() => {
                setPracticeID(row.id);
                setPracticeName(row.practiceName);
                setAction('Update');
              }}
            >
              <BiEdit />
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setPracticeID(row.id);
                setAction('Delete');
              }}
            >
              <BiTrash />
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (selectedDepartment?.id) fetchPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment]);

  function callError(result) {
    if (result.responseCode !== 0) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: result.message}));
      return true;
    }
    return false;
  }

  async function fetchPractices() {
    setLoadingPractices(true);
    let result = await Get(API_PRACTICE + `/${selectedDepartment.id}`);
    if (!callError(result)) {
      setPractices(result.data ?? []);
    } else {
      setPractices([]);
    }
    setLoadingPractices(false);
  }

  function validateEntry() {
    return practiceName.length > 0;
  }

  async function ModifyPractice(event) {
    event.preventDefault();
    setLoadingModify(true);

    let param = {
      departmentID: selectedDepartment?.id,
      practiceName: practiceName,
    };

    let url;
    switch (action) {
      case 'Create':
        param.practiceName = practiceName;
        url = API_PRACTICE + API_ACTION.CREATE;
        break;
      case 'Update':
        param.practiceName = practiceName;
        param.id = practiceID;
        url = API_PRACTICE + API_ACTION.UPDATE;
        break;
      case 'Delete':
        param.practiceName = practiceName;
        param.id = practiceID;
        url = API_PRACTICE + API_ACTION.DELETE;
        break;
    }
    let result = await Post(url, param);
    if (!callError(result)) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: result.message}));
      await fetchPractices();
    } else if (result.responseCode === -9) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: 'The data is being used somewhere else'}));
    } else {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: result.message}));
    }
    setAction('');
    setPracticeID(defaultPractice.id);
    setPracticeName(defaultPractice.practiceName);
    setLoadingModify(false);
  }

  const selection = (
    <div className="row mb-4">
      <div className="col-md-6 col-lg-4 col-sm-12">
        <label className="label">Department</label>
        <DepartmentDropdown
          placeholder={'select a departement'}
          callbackChangeDepartment={(value) => {
            setSelectedDepartment(value);
          }}
          isGetAllDept={true}
          disabled={!isAdmin}
        />
      </div>
    </div>
  );

  const body = (
    <div className="container py-4">
      <Button onClick={() => setAction('Create')}>Add Practice</Button>
      <EssSimpleTable
        columns={columns}
        columnData={columnData}
        filteredData={filteredData}
        renderEmptyData={MemoizedEmptyRows}
        dataCount={{length: practices.length}}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onPageOption={pageOptions}
        sortTableDirection={''}
        sortKey={''}
        isLoading={loadingPractices}
      />
    </div>
  );

  const deletePrompt = (
    <Modal show={action === 'Delete'} centered size="md" aria-labelledby="contained-modal-title-vcenter">
      <Modal.Header>
        <Modal.Title>Delete Practice</Modal.Title>
      </Modal.Header>
      <Modal.Body>Are you sure to delete this practice?</Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={ModifyPractice} disabled={loadingModify}>
          {loadingModify ? (
            <div>
              <span className="spinner-border spinner-border-sm modal-spinner" role="status" aria-hidden="true"></span>
            </div>
          ) : (
            'Yes'
          )}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setAction('');
            setPracticeID(0);
          }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const createOrUpdatePrompt = (
    <Modal
      show={['Create', 'Update'].includes(action)}
      centered
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
    >
      <Form onSubmit={ModifyPractice}>
        <Modal.Header>
          <Modal.Title>{action} Practice</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row g-3>
            <Col>
              <Form.Group size="lg" controlId="username" className="mb-2">
                <Form.Label>Practice Name</Form.Label>
                <Form.Control
                  autoFocus
                  placeholder="Practice"
                  type="text"
                  value={practiceName}
                  onChange={(e) => setPracticeName(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button size="md" type="submit" className="modal-footer-button" disabled={!validateEntry() || loadingModify}>
            {loadingModify ? (
              <div>
                <span
                  className="spinner-border spinner-border-sm modal-spinner"
                  role="status"
                  aria-hidden="true"
                ></span>
              </div>
            ) : (
              'Save Changes'
            )}
          </Button>

          <Button
            variant="secondary"
            onClick={() => {
              setAction('');
              setPracticeID(defaultPractice.id);
              setPracticeName(defaultPractice.practiceName);
            }}
            disabled={loadingModify}
          >
            Close
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );

  return (
    <div className="container manage-practices">
      {selection}
      {body}
      {deletePrompt}
      {createOrUpdatePrompt}
    </div>
  );
}
